/**
 * Inbound queue — dock operator list.
 *
 * Rows with dock arrival tags sort to the top, then crate in progress, then expected date.
 * Operators open receive by tapping a dock unit tile, not the order header.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, PackageSearch, RefreshCw } from "lucide-react";

import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintContent, PageTitleWithHelp } from "@/components/ui/title-help";
import { useAuth } from "@/components/AuthProvider";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { PrePackCartonOperatorStack } from "@/pages/prepack/PrePackCartonOperatorStack";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { playPairFailureSound, playPairSuccessSound } from "@/lib/floorStationPairAudio";
import { cn } from "@/lib/utils";
import { buildInboundReceiveNavigateTarget } from "@/pages/shipping/inboundReceiveNavigation";
import {
  InboundReceiveArrivalOrderPickModal,
  type InboundArrivalOrderPickSource,
} from "@/pages/shipping/InboundReceiveArrivalOrderPickModal";
import { InboundReceiveArrivalRouteModal } from "@/pages/shipping/InboundReceiveArrivalRouteModal";
import { reprintInboundReceiveArrivalPackageLabel } from "@/pages/shipping/inboundReceiveArrivalLabelPrint";
import { navigateAfterInboundReceiveRouteApplied } from "@/pages/shipping/inboundReceiveRouteNavigation";
import { inboundReceiveRouteUsesSpecialDesk, type InboundReceiveRoute } from "@/pages/shipping/inboundReceiveRoute";
import { InboundZplPrinterHeaderButton } from "./InboundZplPrinterHeaderButton";
import {
  formatInboundArrivalUnitHeading,
  parseInboundArrivalUnitKind,
} from "./inboundArrivalUnitKind";
import { isInboundRowAllContainersFinished, isInboundRowFullyReceived } from "./inboundQueueFilters";
import { inboundReceiveOperatorsOnCrate } from "./inboundReceiveCrateOperators";
import { useInboundZplPrinter } from "./inboundZplPrinter";
import {
  applyInboundReceiveArrivalRoute,
  fetchInboundOrders,
  fetchInboundReceiveOrderOperators,
  formatInboundExpectedDates,
  formatInboundSourceType,
  inboundReceiveOrderOperatorsQueryKey,
  lookupReceiveArrivalScan,
  type InboundListRow,
  type InboundQueueArrivalContainer,
  type InboundReceiveOrderOperator,
} from "./inboundApi";

const QUEUE_PAGE_HOVER_HINT =
  "Receive station: tap a dock unit (crate, carton, etc.) to open that shipment for counting. "
  + "Tap the printer icon to choose the label printer; the choice carries into the receive workspace. "
  + "Scan an ARRIVED tag to jump straight to the linked order and unit, or pick from the list below. "
  + "Print a clear inventory tag for To stock, or a HOLD tag when damaged or wrong SKU sends lines to Holding. "
  + "IQC and packing slip mismatches are worked on Holding, not here.";

function hasDockArrivals(row: InboundListRow): boolean {
  return (row.arrival_containers ?? []).length > 0;
}

function hasReceiptInProgress(row: InboundListRow): boolean {
  return (row.arrival_containers ?? []).some(
    (container) => container.draft_receipt_id != null && container.received_at == null,
  );
}

function operatorSort(left: InboundListRow, right: InboundListRow): number {
  const leftHasArrivals = hasDockArrivals(left) ? 0 : 1;
  const rightHasArrivals = hasDockArrivals(right) ? 0 : 1;
  if (leftHasArrivals !== rightHasArrivals) return leftHasArrivals - rightHasArrivals;
  const leftInProgress = hasReceiptInProgress(left) ? 0 : 1;
  const rightInProgress = hasReceiptInProgress(right) ? 0 : 1;
  if (leftInProgress !== rightInProgress) return leftInProgress - rightInProgress;
  const leftDate = left.expected_sort_date ?? "9999-12-31";
  const rightDate = right.expected_sort_date ?? "9999-12-31";
  if (leftDate !== rightDate) return leftDate < rightDate ? -1 : 1;
  return (left.source_number ?? "").localeCompare(right.source_number ?? "");
}

function formatArrivalCurrentLocationLabel(
  location: InboundQueueArrivalContainer["current_location"],
): string | null {
  switch (location) {
    case "holding":
      return "Holding";
    case "to_stock":
      return "To Stock";
    case "shelf":
      return "Shelf";
    default:
      return null;
  }
}

function formatArrivalContainerLoggedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function arrivalContainerStatus(container: InboundQueueArrivalContainer): {
  label: string;
  className: string;
} {
  if (container.received_at != null) {
    return {
      label: "Finished",
      className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    };
  }
  if (container.draft_receipt_id != null) {
    return {
      label: "Receipt in progress",
      className: "bg-brand-50 text-brand-700 ring-brand-200",
    };
  }
  return {
    label: "Awaiting receive",
    className: "bg-sky-50 text-sky-700 ring-sky-200",
  };
}

function inboundReceiveOrderOperatorKey(sourceType: InboundListRow["source_type"], sourceId: string): string {
  return `${sourceType}:${sourceId}`;
}

function inboundQueueRowKey(row: InboundListRow): string {
  if (row.source_ids != null && row.source_ids.length > 1) {
    return `${row.source_type}-combined-${row.source_ids.join(",")}`;
  }
  return `${row.source_type}-${row.source_id}`;
}

function operatorsForInboundQueueRow(
  row: InboundListRow,
  operatorsBySourceKey: ReadonlyMap<string, InboundReceiveOrderOperator[]>,
): InboundReceiveOrderOperator[] {
  const sourceIds =
    row.source_ids != null && row.source_ids.length > 0 ? row.source_ids : [row.source_id];
  if (sourceIds.length <= 1) {
    return operatorsBySourceKey.get(inboundReceiveOrderOperatorKey(row.source_type, row.source_id)) ?? [];
  }
  const seen = new Set<string>();
  const combined: InboundReceiveOrderOperator[] = [];
  for (const sourceId of sourceIds) {
    const list = operatorsBySourceKey.get(inboundReceiveOrderOperatorKey(row.source_type, sourceId)) ?? [];
    for (const op of list) {
      const key = `${op.userId}:${op.arrivalPackageId ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      combined.push(op);
    }
  }
  combined.sort((left, right) => left.email.localeCompare(right.email));
  return combined;
}

function InboundQueueRow({
  row,
  operators,
  viewerUserId,
}: {
  row: InboundListRow;
  operators: InboundReceiveOrderOperator[];
  viewerUserId: string | null;
}): JSX.Element {
  const arrivalContainers = row.arrival_containers ?? [];
  const submittedUnpostedQuantity = Number(row.submitted_unposted_quantity) || 0;
  const fullyReceived = isInboundRowFullyReceived(row);

  return (
    <div className="rounded-lg border-2 border-slate-200 bg-white shadow-sm transition">
      <div className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-lg font-semibold text-slate-900">
                {row.source_number ?? `${formatInboundSourceType(row.source_type)} ${row.source_id}`}
              </span>
              <HoverHintContent
                hoverHint={row.supplier_or_origin ?? "Unknown vendor"}
                cursor="default"
                className="shrink-0"
              >
                <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-800 ring-1 ring-slate-200">
                  {row.supplier_or_origin ?? "Unknown vendor"}
                </span>
              </HoverHintContent>
              {fullyReceived ? (
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                  Fully received
                </span>
              ) : null}
              {row.source_type !== "purchase_order" && submittedUnpostedQuantity > 0 ? (
                <HoverHintContent
                  hoverHint="Counted and submitted at the dock; Megaventory inventory updates when accounting posts the invoice."
                  cursor="default"
                  className="shrink-0"
                >
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                    Awaiting MV post
                  </span>
                </HoverHintContent>
              ) : null}
            </div>
            <p className="mt-0.5 truncate text-sm text-slate-600">
              {formatInboundSourceType(row.source_type)} · Expected {formatInboundExpectedDates(row.expected_dates)} ·{" "}
              {row.line_count} line{row.line_count === "1" ? "" : "s"}
              {arrivalContainers.length > 0 ? (
                <> · Tap a unit below to receive</>
              ) : (
                <> · No dock tags yet</>
              )}
            </p>
          </div>
        </div>
      </div>
      {arrivalContainers.length > 0 ? (
        <div className="grid grid-cols-4 gap-2 border-t border-slate-100 px-3 py-3 min-[1100px]:grid-cols-6">
          {arrivalContainers.map((container) => {
            const status = arrivalContainerStatus(container);
            const crateInProgress =
              container.draft_receipt_id != null && container.received_at == null;
            const unitHeading = formatInboundArrivalUnitHeading(
              parseInboundArrivalUnitKind(container.unit_kind),
              container.package_no,
            );
            const receiveTarget = buildInboundReceiveNavigateTarget(
              row.source_type,
              String(row.source_id),
              {
                arrivalPackageId: container.package_id,
                arrivalPackageNo: container.package_no,
                arrivalScan: container.scan_token,
                arrivalUnitKind: parseInboundArrivalUnitKind(container.unit_kind),
              },
            );
            const locationLabel =
              container.received_at != null
                ? formatArrivalCurrentLocationLabel(container.current_location)
                : null;
            return (
              <Link
                key={container.package_id}
                to={{ pathname: receiveTarget.pathname, search: receiveTarget.search }}
                state={receiveTarget.state}
                title={`${unitHeading} · Logged ${formatArrivalContainerLoggedAt(container.arrived_at)}`}
                className={cn(
                  "flex min-h-[5.5rem] flex-col justify-center gap-1 rounded-lg border-2 bg-slate-50 px-3 py-3 text-left",
                  crateInProgress
                    ? "border-brand-400 bg-brand-50/40 hover:border-brand-500 hover:bg-brand-50/60"
                    : "border-slate-200 hover:border-brand-300 hover:bg-white",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-lg font-bold leading-tight text-slate-900">{unitHeading}</span>
                  <PrePackCartonOperatorStack
                    operators={inboundReceiveOperatorsOnCrate(operators, container.package_id)}
                    viewerUserId={viewerUserId}
                    className="shrink-0"
                  />
                </div>
                <span className="font-mono text-sm leading-snug text-slate-600 whitespace-pre-wrap min-[800px]:text-base">
                  {container.scan_label}
                </span>
                <span
                  className={cn(
                    "mt-0.5 w-fit rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset",
                    status.className,
                  )}
                >
                  {status.label}
                </span>
                {locationLabel != null ? (
                  <span className="w-fit text-xs font-semibold text-slate-700">{locationLabel}</span>
                ) : null}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function InboundQueuePage(): JSX.Element {
  usePageBreadcrumb("Receive");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const viewerUserId = session?.user?.id ?? null;
  const scanRef = useRef<HTMLInputElement | null>(null);
  const [showPurchaseOrders, setShowPurchaseOrders] = useState(true);
  const [showTransfers, setShowTransfers] = useState(true);
  const [showAllContainersFinished, setShowAllContainersFinished] = useState(false);
  const [searchParams] = useSearchParams();
  const openedArrivalScan = useRef("");
  const [query, setQuery] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [arrivalOrderPick, setArrivalOrderPick] = useState<{
    scan: string;
    packageId: string;
    packageNo: number;
    vendorName: string | null;
    unitKind: string;
    sources: InboundArrivalOrderPickSource[];
  } | null>(null);
  const [arrivalRouteAssociation, setArrivalRouteAssociation] = useState<{
    scan: string;
    packageId: string;
    packageNo: number;
    arrivalId: string;
    vendorName: string | null;
    unitKind: string;
    initialRoute: InboundReceiveRoute | null;
  } | null>(null);
  const { station, setStation, selectedPrinter, unavailableMessage, documents } = useInboundZplPrinter();
  const inboundQuery = useQuery({
    queryKey: ["shipping", "inbound"],
    queryFn: fetchInboundOrders,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const receiveOrderOperatorsQuery = useQuery({
    queryKey: inboundReceiveOrderOperatorsQueryKey,
    queryFn: fetchInboundReceiveOrderOperators,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const operatorsBySourceKey = useMemo(() => {
    const map = new Map<string, InboundReceiveOrderOperator[]>();
    for (const operator of receiveOrderOperatorsQuery.data ?? []) {
      const key = inboundReceiveOrderOperatorKey(operator.sourceType, String(operator.sourceId));
      const list = map.get(key) ?? [];
      list.push(operator);
      map.set(key, list);
    }
    for (const [, list] of map) {
      list.sort((left, right) => left.email.localeCompare(right.email));
    }
    return map;
  }, [receiveOrderOperatorsQuery.data]);

  const rows = inboundQuery.data ?? [];
  const filteredRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows
      .filter((row) => {
        if (row.source_type === "purchase_order" && !showPurchaseOrders) return false;
        if (row.source_type === "transfer_order" && !showTransfers) return false;
        if (!showAllContainersFinished && isInboundRowAllContainersFinished(row)) return false;
        if (!needle) return true;
        return [row.source_number, row.supplier_or_origin, row.source_status, row.search_text]
          .concat(
            (row.arrival_containers ?? []).flatMap((container) => [
              String(container.package_no),
              container.scan_label,
              container.scan_token,
            ]),
          )
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle);
      })
      .sort(operatorSort);
  }, [query, rows, showAllContainersFinished, showPurchaseOrders, showTransfers]);

  const loadError = inboundQuery.isError && inboundQuery.error instanceof Error ? inboundQuery.error.message : null;

  const applyRouteMutation = useMutation({
    mutationFn: (input: {
      scan: string;
      receive_route: InboundReceiveRoute;
      purchaseOrderId?: number;
      transferOrderId?: number;
      rmaId?: number;
      deviceSerials?: string[];
      unitKind: string;
      arrivalId: string;
      packageId: string;
    }) =>
      applyInboundReceiveArrivalRoute({
        scan: input.scan,
        receive_route: input.receive_route,
        purchaseOrderId: input.purchaseOrderId,
        transferOrderId: input.transferOrderId,
        rmaId: input.rmaId,
        deviceSerials: input.deviceSerials,
      }),
    onSuccess: async (result, variables) => {
      playPairSuccessSound();
      setScanError(null);
      setArrivalRouteAssociation(null);
      void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound"] });
      if (result.receive_route === "rma" && selectedPrinter.trim() !== "") {
        try {
          await reprintInboundReceiveArrivalPackageLabel({
            arrivalId: result.arrival_id,
            packageId: result.package_id,
            printer: selectedPrinter,
          });
        } catch (printError) {
          setScanError(
            printError instanceof Error
              ? `Routed, but arrival tag did not print: ${printError.message}`
              : "Routed, but arrival tag did not print.",
          );
        }
      }
      navigateAfterInboundReceiveRouteApplied(navigate, {
        receive_route: result.receive_route,
        resolved_source: result.resolved_source,
        package_id: result.package_id,
        package_no: result.package_no,
        arrival_id: result.arrival_id,
        rma_number: result.rma_number,
        rma_count_checked: (variables.deviceSerials?.length ?? 0) > 0,
        scan: variables.scan,
        unitKind: variables.unitKind,
      });
    },
    onError: (error) => {
      playPairFailureSound();
      setScanError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const arrivalScanMutation = useMutation({
    mutationFn: (value: string) => lookupReceiveArrivalScan(value),
    onSuccess: (result, scanned) => {
      if (result.kind === "arrival_package") {
        const resolved = result.resolved_source;
        const pickSources =
          result.resolved_sources.length > 0
            ? result.resolved_sources
            : resolved != null
              ? [resolved]
              : [];
        if (pickSources.length === 0) {
          const presetRoute = result.pkg.receive_route;
          if (
            presetRoute != null &&
            inboundReceiveRouteUsesSpecialDesk(presetRoute) &&
            presetRoute !== "rma"
          ) {
            playPairSuccessSound();
            setScanError(null);
            navigateAfterInboundReceiveRouteApplied(navigate, {
              receive_route: presetRoute,
              resolved_source: null,
              package_id: result.pkg.package_id,
              package_no: result.pkg.package_no,
              arrival_id: result.pkg.arrival_id,
              rma_number: null,
              scan: scanned.trim(),
              unitKind: result.pkg.unit_kind,
            });
            return;
          }
          if (presetRoute === "rma") {
            playPairSuccessSound();
          } else {
            playPairFailureSound();
          }
          setScanError(null);
          setArrivalRouteAssociation({
            scan: scanned.trim(),
            packageId: result.pkg.package_id,
            packageNo: result.pkg.package_no,
            arrivalId: result.pkg.arrival_id,
            vendorName: result.pkg.vendor_name,
            unitKind: result.pkg.unit_kind,
            initialRoute: presetRoute,
          });
          return;
        }
        playPairSuccessSound();
        setScanError(null);
        setQuery("");
        const allPurchaseOrders =
          pickSources.length > 1
          && pickSources.every((source) => source.source_type === "purchase_order");
        if (pickSources.length > 1 && !allPurchaseOrders) {
          setArrivalOrderPick({
            scan: scanned.trim(),
            packageId: result.pkg.package_id,
            packageNo: result.pkg.package_no,
            vendorName: result.pkg.vendor_name,
            unitKind: result.pkg.unit_kind,
            sources: pickSources,
          });
          return;
        }
        const orderedSources = allPurchaseOrders
          ? [...pickSources].sort(
              (left, right) => Number(left.source_id) - Number(right.source_id),
            )
          : pickSources;
        const only = orderedSources[0]!;
        const target = buildInboundReceiveNavigateTarget(only.source_type, only.source_id, {
          arrivalPackageId: result.pkg.package_id,
          arrivalPackageNo: result.pkg.package_no,
          arrivalScan: scanned.trim(),
          arrivalUnitKind: parseInboundArrivalUnitKind(result.pkg.unit_kind),
        });
        navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
        return;
      }
      if (result.kind === "sku") {
        setQuery(result.sku);
        setScanError(null);
        return;
      }
      playPairFailureSound();
      setScanError("No matching ARRIVED carton for that scan.");
    },
    onError: (error) => {
      playPairFailureSound();
      setScanError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const submitArrivalScan = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === "") return;
    arrivalScanMutation.mutate(trimmed);
  };

  const arrivalScanFromUrl = searchParams.get("scan")?.trim() ?? "";
  useEffect(() => {
    if (arrivalScanFromUrl === "" || openedArrivalScan.current === arrivalScanFromUrl) return;
    openedArrivalScan.current = arrivalScanFromUrl;
    setQuery(arrivalScanFromUrl);
    arrivalScanMutation.mutate(arrivalScanFromUrl);
  }, [arrivalScanFromUrl, arrivalScanMutation]);

  useFloorPageScanCapture({
    enabled: arrivalRouteAssociation == null && arrivalOrderPick == null,
    inputRef: scanRef,
    onScan: (value) => {
      setQuery(value);
      submitArrivalScan(value);
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={QUEUE_PAGE_HOVER_HINT}>Receive</PageTitleWithHelp>
        <div className="flex min-w-0 max-w-full flex-wrap items-end gap-2">
          <LaborStationOperatorPresence stationPage="receiving" receivingFloor="receive" />
          <InboundZplPrinterHeaderButton
            station={station}
            onStationChange={setStation}
            unavailableMessage={unavailableMessage}
          
      documents={documents}
    />
          <Button size="sm" variant="secondary" onClick={() => void inboundQuery.refetch()} disabled={inboundQuery.isFetching}>
            {inboundQuery.isFetching ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            Refresh
          </Button>
        </div>
      </header>

      <Card>
        <CardBody className="space-y-2">
          <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
            <div className="w-[33.75rem] max-w-full shrink-0">
              <Label htmlFor="inbound-queue-query">Search or scan</Label>
              <Input
                id="inbound-queue-query"
                ref={scanRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submitArrivalScan(query);
                  }
                }}
                placeholder="Order number, supplier, SKU, or ARRIVED carton tag"
                autoComplete="off"
                {...FLOOR_PAGE_SCAN_TARGET_PROPS}
              />
            </div>
            <div>
              <Label>Source type</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={showPurchaseOrders ? "primary" : "secondary"}
                  onClick={() => setShowPurchaseOrders((current) => !current)}
                >
                  Purchase orders
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={showTransfers ? "primary" : "secondary"}
                  onClick={() => setShowTransfers((current) => !current)}
                >
                  Transfers
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={showAllContainersFinished ? "primary" : "secondary"}
                  onClick={() => setShowAllContainersFinished((current) => !current)}
                >
                  All containers finished
                </Button>
              </div>
            </div>
            <p className="pb-2 text-xs text-slate-500 sm:ml-auto">
              Showing {filteredRows.length} of {rows.length} inbound order{rows.length === 1 ? "" : "s"}.
            </p>
          </div>
        </CardBody>
      </Card>

      {scanError ? (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {scanError}
        </p>
      ) : null}

      {loadError ? (
        <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          Could not load inbound orders: {loadError}
        </p>
      ) : null}

      {inboundQuery.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading inbound orders...
        </div>
      ) : filteredRows.length === 0 ? (
        <Card>
          <CardBody>
            <div className="flex flex-col items-center py-8 text-center">
              <PackageSearch className="mb-2 h-8 w-8 text-slate-300" aria-hidden />
              <p className="text-sm text-slate-500">No inbound orders match the current filters.</p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredRows.map((row) => (
            <InboundQueueRow
              key={inboundQueueRowKey(row)}
              row={row}
              operators={operatorsForInboundQueueRow(row, operatorsBySourceKey)}
              viewerUserId={viewerUserId}
            />
          ))}
        </div>
      )}
      <InboundReceiveArrivalOrderPickModal
        open={arrivalOrderPick != null}
        packageNo={arrivalOrderPick?.packageNo ?? 0}
        vendorName={arrivalOrderPick?.vendorName}
        sources={arrivalOrderPick?.sources ?? []}
        onSelect={(source) => {
          if (arrivalOrderPick == null) return;
          const target = buildInboundReceiveNavigateTarget(source.source_type, source.source_id, {
            arrivalPackageId: arrivalOrderPick.packageId,
            arrivalPackageNo: arrivalOrderPick.packageNo,
            arrivalScan: arrivalOrderPick.scan,
            arrivalUnitKind: parseInboundArrivalUnitKind(arrivalOrderPick.unitKind),
          });
          setArrivalOrderPick(null);
          navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
        }}
        onCancel={() => setArrivalOrderPick(null)}
      />
      <InboundReceiveArrivalRouteModal
        open={arrivalRouteAssociation != null}
        scan={arrivalRouteAssociation?.scan ?? ""}
        packageNo={arrivalRouteAssociation?.packageNo ?? 0}
        vendorName={arrivalRouteAssociation?.vendorName}
        initialRoute={arrivalRouteAssociation?.initialRoute ?? null}
        pending={applyRouteMutation.isPending}
        onApply={(input) => {
          if (arrivalRouteAssociation == null) return;
          applyRouteMutation.mutate({
            scan: arrivalRouteAssociation.scan,
            receive_route: input.receive_route,
            purchaseOrderId: input.purchaseOrderId,
            transferOrderId: input.transferOrderId,
            rmaId: input.rmaId,
            deviceSerials: input.deviceSerials,
            unitKind: arrivalRouteAssociation.unitKind,
            arrivalId: arrivalRouteAssociation.arrivalId,
            packageId: arrivalRouteAssociation.packageId,
          });
        }}
        onCancel={() => setArrivalRouteAssociation(null)}
      />
    </div>
  );
}
